import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  GlobeAltIcon,
  ScaleIcon,
  CogIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowsRightLeftIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import MultiSelect from '../common/MultiSelect';
import {
  justiceSphereOptions,
  tribunalMatchesJusticeSphere,
} from '../../utils/justiceSphere';

const JUSTICE_TYPE_OPTIONS = justiceSphereOptions();

const SCORE_BAR_COLORS = {
  EXCEPCIONAL: 'bg-purple-500',
  PROVÁVEL: 'bg-green-500',
  POSSÍVEL: 'bg-yellow-500',
  REMOTO: 'bg-orange-500',
  INAPTO: 'bg-red-500',
};

const MonitoringDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [filters, setFilters] = useState({
    period: 'month',
    scoreRange: 'all',
    justiceType: '',
    tribunals: [],
    state: ''
  });

  const simplifyTribunalName = (tribunal, source) => {
    if (source && source.trim()) return source.trim();
    if (!tribunal) return '';
    return tribunal.replace(/Diário do |Diario do /, '').replace(/ - DJN.*/, '').replace(/ - PJE.*/, '');
  };

  useEffect(() => {
    fetchDashboardData(true);
  }, []);

  useEffect(() => {
    if (dashboardData) {
      fetchDashboardData(false);
    }
  }, [filters]);

  const fetchDashboardData = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setUpdating(true);
      }
      const params = new URLSearchParams();
      params.set('period', filters.period);
      params.set('scoreRange', filters.scoreRange);
      if (filters.justiceType) params.set('justiceType', filters.justiceType);
      if (filters.tribunals.length) params.set('tribunals', filters.tribunals.join(','));
      if (filters.state) params.set('state', filters.state);
      const data = await apiService.get(`/monitoring/dashboard?${params}`);
      setDashboardData(data);
    } catch (error) {
      // Error fetching dashboard data
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setUpdating(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados de monitoramento...</p>
        </div>
      </div>
    );
  }

  const { kpis, charts, conversion, filterOptions, operationalHealth } = dashboardData || {};
  const availableTribunals = filterOptions?.tribunals || charts?.tribunalVolume?.map((t) => t.tribunal) || [];
  const availableStates = filterOptions?.states || [];
  const tribunalOptions = availableTribunals
    .filter((tribunal) => tribunalMatchesJusticeSphere(tribunal, filters.justiceType))
    .map((tribunal) => ({
      value: tribunal,
      label: simplifyTribunalName(tribunal),
    }));

  const maxTimelinePubs = Math.max(1, ...(charts?.timeline || []).map((d) => d.publications || d.count || 0));
  const maxTimelineProcs = Math.max(1, ...(charts?.timeline || []).map((d) => d.processes || 0));

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Monitoramento DJEN
          </h1>
          <p className="text-gray-600">
            Publicações e processos unificados
          </p>
        </div>

        {/* Filtros unificados */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3 mb-3">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <span className="text-gray-700 font-semibold">Filtros</span>
            <span className="text-xs text-gray-500">aplicados a publicações e processos</span>
            {updating && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Atualizando...</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              className="bg-white text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="today">Hoje</option>
              <option value="week">Última Semana</option>
              <option value="month">Último Mês</option>
              <option value="quarter">Último Trimestre</option>
              <option value="year">Último Ano</option>
            </select>

            <select
              value={filters.scoreRange}
              onChange={(e) => setFilters({ ...filters, scoreRange: e.target.value })}
              className="bg-white text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="all">Todos os Scores</option>
              <option value="excepcional">EXCEPCIONAL</option>
              <option value="provavel">PROVÁVEL</option>
              <option value="possivel">POSSÍVEL</option>
              <option value="remoto">REMOTO</option>
              <option value="inapto">INAPTO</option>
            </select>

            <select
              value={filters.justiceType}
              onChange={(e) => {
                const justiceType = e.target.value;
                setFilters((current) => ({
                  ...current,
                  justiceType,
                  tribunals: current.tribunals.filter((tribunal) =>
                    tribunalMatchesJusticeSphere(tribunal, justiceType),
                  ),
                }));
              }}
              className="bg-white text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Todos os tipos de justiça</option>
              {JUSTICE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="relative z-20">
              <MultiSelect
                options={tribunalOptions}
                value={filters.tribunals}
                onChange={(tribunals) => setFilters({ ...filters, tribunals })}
                searchable
                placeholder="Todos os Tribunais"
              />
            </div>

            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="bg-white text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">Todos os Estados</option>
              {availableStates.map((state, index) => (
                <option key={index} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="inline-flex items-center gap-1.5 text-blue-700">
              <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Publicações DJEN
            </span>
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Processos
            </span>
          </div>
        </div>

        {/* KPIs unificados */}
        <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4 transition-opacity ${updating ? 'opacity-75' : ''}`}>
          <div className="bg-white border-l-4 border-l-blue-500 border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Publicações</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{kpis?.totalPublications?.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">DJEN no período</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border-l-4 border-l-emerald-500 border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-700 font-medium">Processos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{kpis?.totalProcesses?.toLocaleString() ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">criados no período</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <BriefcaseIcon className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Score médio (pub.)</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{kpis?.avgScore}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <ScaleIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Taxa PROVÁVEL</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{kpis?.provavelRate ?? 0}%</p>
                <p className="text-xs text-gray-500 mt-1">publicações viáveis</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Volume hoje</p>
                <p className="text-xs text-blue-600 mt-1">{kpis?.todayPublications} publicações</p>
                <p className="text-xs text-emerald-600 mt-1">{kpis?.todayProcesses ?? 0} processos</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Crescimento</p>
                <p className={`text-2xl font-bold mt-1 ${kpis?.growthRate >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {kpis?.growthRate >= 0 ? '+' : ''}{kpis?.growthRate}%
                </p>
                <p className="text-xs text-gray-500 mt-1">vs período anterior</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quadro de conversão publicação → processo */}
        <div className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-4 transition-opacity ${updating ? 'opacity-75' : ''}`}>
          <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5 text-amber-600" />
            Conversão: viabilidade da publicação → processos
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Publicações</p>
              <p className="text-3xl font-bold text-blue-900 mt-1">
                {conversion?.totalPublications?.toLocaleString() ?? kpis?.totalPublications?.toLocaleString() ?? 0}
              </p>
              <p className="text-xs text-blue-600 mt-1">total no filtro</p>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Viáveis</p>
              <p className="text-3xl font-bold text-amber-900 mt-1">
                {conversion?.viablePublications?.toLocaleString() ?? 0}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                score ≥ {conversion?.minScoreThreshold ?? 66}
              </p>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Processos</p>
              <p className="text-3xl font-bold text-emerald-900 mt-1">
                {conversion?.processesCreated?.toLocaleString() ?? 0}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                {conversion?.linkedProcesses ?? 0} vinculados a publicação
              </p>
            </div>

            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Taxa de conversão</p>
              <p className="text-3xl font-bold text-purple-900 mt-1">
                {conversion?.conversionRate ?? kpis?.conversionRate ?? 0}%
              </p>
              <p className="text-xs text-purple-600 mt-1">
                processos / publicações viáveis
              </p>
            </div>
          </div>

          {conversion?.byViability?.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Processos por viabilidade</p>
              <div className="flex flex-wrap gap-2">
                {conversion.byViability.map((item, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                      item.viabilidade === 'ALTA'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : item.viabilidade === 'MEDIA' || item.viabilidade === 'MÉDIA'
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                        : item.viabilidade === 'BAIXA'
                        ? 'bg-orange-50 border-orange-200 text-orange-800'
                        : 'bg-gray-50 border-gray-200 text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{item.viabilidade}</span>
                    <span className="font-semibold">{item.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Status Operacional */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-4">
          <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
            <CogIcon className="h-5 w-5 text-gray-500" />
            Status do Sistema de Monitoramento
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${operationalHealth?.syncStatus?.isHealthy ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                {operationalHealth?.syncStatus?.isHealthy ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                )}
                <div>
                  <h4 className="text-gray-900 font-medium">Sincronização DJEN</h4>
                  <p className="text-sm text-gray-600">
                    {operationalHealth?.syncStatus?.isHealthy ? 'Operacional' : 'Com Problemas'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Último sync: {operationalHealth?.syncStatus?.lastSync
                  ? new Date(operationalHealth.syncStatus.lastSync).toLocaleString('pt-BR')
                  : 'N/A'}
              </p>
            </div>

            <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-lg">
              <h4 className="text-gray-900 font-medium mb-2 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-blue-500" />
                Performance 24h (publicações)
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Processadas:</span>
                  <span className="text-blue-700 font-medium">{operationalHealth?.performance?.totalPublications}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Score Médio:</span>
                  <span className="text-blue-600 font-medium">{operationalHealth?.performance?.avgScore}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <h4 className="text-gray-900 font-medium mb-2 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />
                Alertas do Sistema
              </h4>
              {operationalHealth?.alerts?.length > 0 ? (
                <div className="space-y-2">
                  {operationalHealth.alerts.slice(0, 2).map((alert, index) => (
                    <div key={index} className={`p-2 rounded text-xs border ${
                      alert.level === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                    }`}>
                      {alert.message}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-green-600 text-sm">Sistema funcionando normalmente</p>
              )}
            </div>
          </div>
        </div>

        {/* Timeline + Distribuição */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 transition-opacity ${updating ? 'opacity-75' : ''}`}>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-2 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-500" />
              Timeline unificada
            </h3>
            <div className="flex items-center gap-4 text-xs mb-3">
              <span className="inline-flex items-center gap-1.5 text-blue-700">
                <span className="w-2 h-2 rounded-sm bg-blue-500" /> Publicações
              </span>
              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                <span className="w-2 h-2 rounded-sm bg-emerald-500" /> Processos
              </span>
            </div>
            <div className="h-60 overflow-x-auto">
              {(charts?.timeline?.length || 0) === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  Sem dados no período
                </div>
              ) : (
                <div className="flex items-end gap-1 min-w-max h-full pt-3 pb-10 box-border">
                  {charts.timeline.map((item, index) => {
                    const pubH = Math.min(
                      ((item.publications || item.count || 0) / maxTimelinePubs) * 100,
                      92,
                    );
                    const procH = Math.min(
                      ((item.processes || 0) / maxTimelineProcs) * 70,
                      92,
                    );
                    return (
                      <div key={index} className="flex flex-col items-center gap-1 w-5.5 group relative">
                        <div className="flex items-end gap-0.5 h-40">
                          <div
                            className="w-2.5 bg-blue-500 rounded-t"
                            style={{ height: `${Math.max(pubH, 2)}%` }}
                            title={`${item.date}: ${item.publications || item.count || 0} publicações`}
                          />
                          <div
                            className="w-2.5 bg-emerald-500 rounded-t"
                            style={{ height: `${Math.max(procH, item.processes ? 2 : 0)}%` }}
                            title={`${item.date}: ${item.processes || 0} processos`}
                          />
                        </div>
                        <p className="text-[9px] text-gray-400 whitespace-nowrap -rotate-45 origin-top-left mt-4">
                          {item.date
                            ? `${item.date.slice(8, 10)}/${item.date.slice(5, 7)}`
                            : ''}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">{charts?.timeline?.length || 0} dias com publicações</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <ScaleIcon className="h-5 w-5 text-blue-500" />
              Classificação por relevância
              <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                publicações
              </span>
            </h3>
            <div className="space-y-3">
              {charts?.scoreDistribution?.map((item, index) => {
                const maxCount = Math.max(...charts.scoreDistribution.map((i) => i.count), 1);
                const widthPercent = (item.count / maxCount) * 100;

                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-700 w-24 text-sm font-medium">{item.classification}</span>
                      <div className="w-36 bg-blue-100 rounded-full h-3">
                        <div
                          className={`${SCORE_BAR_COLORS[item.classification] || 'bg-gray-400'} h-3 rounded-full`}
                          style={{ width: `${widthPercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-blue-700 font-semibold">{item.count}</span>
                      <span className="text-xs text-gray-500 ml-2">Score: {item.avgScore}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tribunais + Classificações */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 transition-opacity ${updating ? 'opacity-75' : ''}`}>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-900 text-lg font-semibold flex items-center gap-2">
                <GlobeAltIcon className="h-5 w-5 text-gray-500" />
                Tribunais mais ativos
              </h3>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-blue-700">Pub.</span>
                <span className="text-emerald-700">Proc.</span>
              </div>
            </div>
            <div className="space-y-2">
              {charts?.tribunalVolume?.slice(0, 8).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-900 text-sm font-medium">
                      {simplifyTribunalName(item.tribunal, item.source)}
                    </span>
                    {item.avgScore != null && (
                      <div className="text-xs text-blue-600 mt-0.5">score médio {item.avgScore}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <div className="text-blue-600 font-semibold">{item.publications ?? item.count}</div>
                      <div className="text-[10px] text-blue-500">publicações</div>
                    </div>
                    <div className="w-px h-8 bg-gray-200" />
                    <div>
                      <div className="text-emerald-600 font-semibold">{item.processes ?? 0}</div>
                      <div className="text-[10px] text-emerald-500">processos</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-gray-900 text-lg font-semibold mb-4 flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              Classificações jurídicas
              <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                publicações
              </span>
            </h3>
            <div className="space-y-2">
              {charts?.classifications?.slice(0, 8).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50/40 border border-blue-100 rounded-lg hover:bg-blue-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 text-sm">{item.classification}</span>
                      {item.priority && (
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${
                          item.priority === 'Alta' || item.priority === 'URGENTE'
                            ? 'bg-red-50 border-red-200 text-red-700'
                            : item.priority === 'Normal'
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-gray-100 border-gray-200 text-gray-600'
                        }`}>
                          {item.priority}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-blue-600 font-semibold">{item.count}</div>
                    <div className="text-xs text-blue-500">publicações</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center text-gray-500 text-sm">
          <p>Sistema de Monitoramento ECP Capital • Sincronização automática com DJEN • Atualizado em tempo real</p>
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;
